import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { fetchCatalogProgressMeta, searchCatalog } from "../api/catalog-api";
import type { ApiClient } from "../api/client";
import { apiClient } from "../api/client";
import { ApiError, userFacingMessage } from "../api/errors";
import type { CatalogUiSort, PublicRelease } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import {
  catalogFingerprint,
  catalogYearOptions,
  defaultCatalogFilters,
  yearFromFilter,
  type CatalogFilters,
} from "./catalog-filters";
import { CatalogContext } from "./CatalogContext";

type CatalogProviderProps = {
  children: ReactNode;
  client?: ApiClient;
};

function filtersEqual(a: CatalogFilters, b: CatalogFilters): boolean {
  return (
    a.query === b.query &&
    a.year === b.year &&
    a.sort === b.sort &&
    a.rareOnly === b.rareOnly
  );
}

export function CatalogProvider({
  children,
  client = apiClient,
}: CatalogProviderProps) {
  const auth = useAuth();
  const authStatus = auth.status;
  const [filters, setFilters] = useState<CatalogFilters>(defaultCatalogFilters);
  const [years, setYears] = useState<number[]>([]);
  const [draftQuery, setDraftQuery] = useState("");
  const [items, setItems] = useState<PublicRelease[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedFingerprint, setLoadedFingerprint] = useState<string | null>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const filtersRef = useRef(filters);
  const loadedFingerprintRef = useRef<string | null>(null);
  filtersRef.current = filters;
  loadedFingerprintRef.current = loadedFingerprint;

  const fingerprint = catalogFingerprint(filters);

  const resetSession = useCallback(() => {
    abortRef.current?.abort();
    requestIdRef.current += 1;
    const defaults = defaultCatalogFilters();
    setFilters(defaults);
    setDraftQuery("");
    setItems([]);
    setNextCursor(null);
    setLoading(false);
    setLoadingMore(false);
    setError(null);
    setLoadedFingerprint(null);
    setScrollTop(0);
    filtersRef.current = defaults;
    loadedFingerprintRef.current = null;
  }, []);

  useEffect(() => {
    if (authStatus === "unauthenticated") {
      resetSession();
    }
  }, [authStatus, resetSession]);

  useEffect(() => {
    if (authStatus !== "authenticated") {
      setYears([]);
      return;
    }
    let active = true;
    void fetchCatalogProgressMeta(client)
      .then((meta) => {
        if (active) {
          setYears(catalogYearOptions(meta.years.map((year) => year.year)));
        }
      })
      .catch(() => {
        if (active) {
          setYears([]);
        }
      });
    return () => {
      active = false;
    };
  }, [authStatus, client]);

  const runSearch = useCallback(
    async (nextFilters: CatalogFilters, cursor?: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const requestId = ++requestIdRef.current;
      const append = Boolean(cursor);
      const nextFingerprint = catalogFingerprint(nextFilters);

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setItems([]);
        setNextCursor(null);
        setLoadedFingerprint(null);
      }
      setError(null);

      try {
        const response = await searchCatalog(
          client,
          {
            q: nextFilters.query.trim() || undefined,
            year: yearFromFilter(nextFilters.year),
            sort: nextFilters.sort,
            rare: nextFilters.rareOnly ? true : undefined,
            cursor,
          },
          { signal: controller.signal },
        );

        if (requestId !== requestIdRef.current) {
          return;
        }

        setItems((prev) => {
          if (!append) {
            return response.items;
          }
          const seen = new Set(prev.map((item) => item.releaseId));
          const merged = [...prev];
          for (const item of response.items) {
            if (!seen.has(item.releaseId)) {
              merged.push(item);
            }
          }
          return merged;
        });
        setNextCursor(response.nextCursor);
        setLoadedFingerprint(nextFingerprint);
        loadedFingerprintRef.current = nextFingerprint;
      } catch (err) {
        if (controller.signal.aborted) {
          return;
        }
        if (requestId !== requestIdRef.current) {
          return;
        }
        if (err instanceof ApiError && err.code === "AUTH_REQUIRED") {
          return;
        }
        setError(userFacingMessage(err));
        if (!append) {
          setItems([]);
          setNextCursor(null);
          setLoadedFingerprint(null);
          loadedFingerprintRef.current = null;
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [client],
  );

  const ensureResults = useCallback(async () => {
    const current = filtersRef.current;
    const currentFingerprint = catalogFingerprint(current);
    if (loadedFingerprintRef.current === currentFingerprint) {
      return;
    }
    await runSearch(current);
  }, [runSearch]);

  const loadMore = useCallback(async () => {
    const current = filtersRef.current;
    const currentFingerprint = catalogFingerprint(current);
    if (loadedFingerprintRef.current !== currentFingerprint) {
      return;
    }
    if (!nextCursor || loading || loadingMore) {
      return;
    }
    await runSearch(current, nextCursor);
  }, [loading, loadingMore, nextCursor, runSearch]);

  const applyFilters = useCallback((next: CatalogFilters) => {
    setFilters((prev) => (filtersEqual(prev, next) ? prev : next));
    setDraftQuery(next.query);
    setScrollTop(0);
  }, []);

  const commitQuery = useCallback((value?: string) => {
    const nextQuery = (value ?? draftQuery).trim();
    setDraftQuery(nextQuery);
    setFilters((prev) => {
      if (prev.query === nextQuery) {
        return prev;
      }
      return { ...prev, query: nextQuery };
    });
    setScrollTop(0);
  }, [draftQuery]);

  const setYear = useCallback((year: string) => {
    setFilters((prev) => (prev.year === year ? prev : { ...prev, year }));
    setScrollTop(0);
  }, []);

  const setSort = useCallback((sort: CatalogUiSort) => {
    setFilters((prev) => (prev.sort === sort ? prev : { ...prev, sort }));
    setScrollTop(0);
  }, []);

  const setRareOnly = useCallback((rareOnly: boolean) => {
    setFilters((prev) => (prev.rareOnly === rareOnly ? prev : { ...prev, rareOnly }));
    setScrollTop(0);
  }, []);

  const resetFilters = useCallback(() => {
    const defaults = defaultCatalogFilters();
    setFilters(defaults);
    setDraftQuery("");
    setScrollTop(0);
  }, []);

  const patchRelease = useCallback((release: PublicRelease) => {
    setItems((prev) =>
      prev.map((item) =>
        item.releaseId === release.releaseId
          ? {
              ...item,
              primaryImage: release.primaryImage,
            }
          : item,
      ),
    );
  }, []);

  const value = useMemo(
    () => ({
      filters,
      years,
      draftQuery,
      items,
      nextCursor,
      loading,
      loadingMore,
      error,
      fingerprint,
      loadedFingerprint,
      scrollTop,
      setDraftQuery,
      commitQuery,
      setYear,
      setSort,
      setRareOnly,
      applyFilters,
      resetFilters,
      ensureResults,
      loadMore,
      setScrollTop,
      patchRelease,
    }),
    [
      applyFilters,
      commitQuery,
      draftQuery,
      error,
      filters,
      years,
      fingerprint,
      items,
      loadedFingerprint,
      loading,
      loadingMore,
      loadMore,
      nextCursor,
      ensureResults,
      resetFilters,
      scrollTop,
      patchRelease,
    ],
  );

  return (
    <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>
  );
}
